import DOMPurify from 'dompurify';

/**
 * Санитизация HTML контента для защиты от XSS атак
 */
export class MessageSanitizer {
  
  /**
   * Базовая санитизация - удаляет все HTML теги
   */
  static sanitizeText(content: string): string {
    if (!content) return '';
    
    // Удаляем все HTML теги, оставляем только текст
    return DOMPurify.sanitize(content, { 
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: []
    });
  }

  /**
   * Санитизация с разрешенными безопасными тегами
   * Для случаев когда нужно сохранить базовое форматирование
   */
  static sanitizeHTML(content: string): string {
    if (!content) return '';
    
    // Разрешаем только безопасные теги для форматирования
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p'],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true
    });
  }

  /**
   * Санитизация для превью сообщений в списке чатов
   */
  static sanitizePreview(content: string, maxLength: number = 100): string {
    if (!content) return '';
    
    // Удаляем HTML и обрезаем до нужной длины
    const cleaned = this.sanitizeText(content);
    
    if (cleaned.length <= maxLength) {
      return cleaned;
    }
    
    return cleaned.substring(0, maxLength).trim() + '...';
  }

  /**
   * Экранирование специальных символов для безопасного вывода
   */
  static escapeHTML(text: string): string {
    if (!text) return '';
    
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Проверка содержит ли строка потенциально опасный контент
   */
  static containsUnsafeContent(content: string): boolean {
    if (!content) return false;
    
    // Проверяем на наличие подозрительных паттернов
    const dangerousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /data:text\/html/i
    ];
    
    return dangerousPatterns.some(pattern => pattern.test(content));
  }
}

/**
 * Хук для безопасного отображения контента
 */
export const useSafeContent = () => {
  
  const sanitizeMessage = (content: string, allowBasicHTML = false): string => {
    if (allowBasicHTML) {
      return MessageSanitizer.sanitizeHTML(content);
    }
    return MessageSanitizer.sanitizeText(content);
  };

  const sanitizePreview = (content: string, maxLength?: number): string => {
    return MessageSanitizer.sanitizePreview(content, maxLength);
  };

  const isContentSafe = (content: string): boolean => {
    return !MessageSanitizer.containsUnsafeContent(content);
  };

  return {
    sanitizeMessage,
    sanitizePreview,
    isContentSafe
  };
};
